import React, { useState, useEffect } from 'react';
import { buildRoadRouteForStops, calculateStraightLineDistanceKm, normalizeLatLng } from './mapUtils';

// =====================================================
// Utilidades compartidas de Logística
// Basado en el Logistics.js actual que ya está funcionando
// =====================================================

// --- Constantes para seguimiento ---
export const TRANSPORTER_UPDATE_INTERVAL_MS = 5 * 60 * 1000;
export const PROXIMITY_RADIUS_METERS = 100;

// --- Componente de Icono ---
export const Icon = ({ path, className = "w-6 h-6", spin = false }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={`${className} ${spin ? 'animate-spin' : ''}`}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

// --- Hook responsive ---
export const useIsMobile = () => {
  const getIsMobile = () =>
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false;

  const [isMobile, setIsMobile] = useState(getIsMobile());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

// --- Fecha local YYYY-MM-DD ---
export const getLocalDateAsString = (date) => {
  if (!date) return '';

  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// --- Estado de ruta unificado ---
export const getRouteStatus = (route) => {
  const normalizedStatus = String(route?.status || '')
    .trim()
    .toLowerCase();
  const destinations = Array.isArray(route?.destinations) ? route.destinations : [];
  const resolvedStatuses = ['completed', 'incomplete', 'cancelled'];
  const hasPendingInboundValidation =
    Number(route?.pendingInboundValidationCount || 0) > 0 ||
    route?.awaitingInboundValidation === true;
  const hasManualClosure =
    Boolean(route?.finishedAt) ||
    Boolean(route?.completedAt) ||
    Boolean(route?.closedAt);
  const hasInactiveFlags = route?.active === false || route?.isActive === false;

  if (normalizedStatus === 'cancelled') {
    return 'cancelled';
  }

  // Algunas rutas pueden arrastrar timestamps de cierre antes del clic manual en
  // "Finalizar ruta". Solo las tomamos como cerradas si también quedaron
  // marcadas como inactivas o si el estado explícito ya dice completed.
  if (normalizedStatus === 'completed' || (hasManualClosure && hasInactiveFlags)) {
    return 'completed';
  }

  if (hasPendingInboundValidation) {
    return 'in_progress';
  }

  if (destinations.some((d) => d.status === 'en_camino' || d.status === 'en_destino')) {
    return 'in_progress';
  }

  const resolvedDestinations = destinations.filter((d) => resolvedStatuses.includes(d.status));
  const hasResolvedDestinations = resolvedDestinations.length > 0;

  if (destinations.length > 0 && resolvedDestinations.length === destinations.length) {
    if (normalizedStatus === 'cancelled') {
      return 'cancelled';
    }
    return 'in_progress';
  }

  if (hasResolvedDestinations) {
    return 'in_progress';
  }

  if (!route || destinations.length === 0) {
    return 'pending';
  }

  if (normalizedStatus === 'programmed') return 'programmed';
  if (normalizedStatus === 'in_progress') return 'in_progress';
  if (normalizedStatus === 'pending') return 'pending';

  return normalizedStatus || 'pending';
};

// --- Catálogo base ---
export const ARGENTINA_PROVINCES = [
  'Buenos Aires',
  'CABA',
  'Catamarca',
  'Chaco',
  'Chubut',
  'Córdoba',
  'Corrientes',
  'Entre Ríos',
  'Formosa',
  'Jujuy',
  'La Pampa',
  'La Rioja',
  'Mendoza',
  'Misiones',
  'Neuquén',
  'Río Negro',
  'Salta',
  'San Juan',
  'San Luis',
  'Santa Cruz',
  'Santa Fe',
  'Santiago del Estero',
  'Tierra del Fuego',
  'Tucumán'
];

export const OFFICE_LOCATION = {
  lat: -34.724019,
  lng: -58.397501,
  name: 'Oficina Central'
};


// --- Carga operativa y roles de destino ---
export const DESTINATION_OPERATION_ROLES = [
  'pickup',
  'delivery',
  'storage',
  'transfer',
  'return',
  'mixed'
];

export const OPERATIONAL_LOAD_UNITS = [
  'unidad',
  'caja',
  'bulto',
  'sobre',
  'equipo',
  'paquete',
  'documento'
];

export const OPERATIONAL_LOAD_OPERATION_TYPES = [
  'pickup',
  'delivery',
  'transfer',
  'storage',
  'return'
];

export const STOCK_OPERATION_REASONS = [
  'sale',
  'installation',
  'loan',
  'replacement',
  'warranty',
  'return',
  'transfer'
];

const DESTINATION_OPERATION_ROLE_LABELS = {
  pickup: 'Retiro',
  delivery: 'Entrega',
  storage: 'Guarda',
  transfer: 'Traslado',
  return: 'Devolución',
  mixed: 'Mixto'
};

const OPERATIONAL_LOAD_STATUS_LABELS = {
  planned: 'Planificada',
  pending_pickup: 'Pendiente de retiro',
  picked_up: 'Retirada',
  in_transit: 'En tránsito',
  stored: 'Guardada',
  delivered: 'Entregada',
  returned: 'Devuelta',
  incident: 'Incidencia',
  cancelled: 'Cancelada'
};

const OPERATIONAL_LOAD_STATUS_TONES = {
  planned: 'border-slate-200 bg-slate-100 text-slate-700',
  pending_pickup: 'border-amber-200 bg-amber-100 text-amber-700',
  picked_up: 'border-sky-200 bg-sky-100 text-sky-700',
  in_transit: 'border-cyan-200 bg-cyan-100 text-cyan-700',
  stored: 'border-violet-200 bg-violet-100 text-violet-700',
  delivered: 'border-emerald-200 bg-emerald-100 text-emerald-700',
  returned: 'border-orange-200 bg-orange-100 text-orange-700',
  incident: 'border-rose-200 bg-rose-100 text-rose-700',
  cancelled: 'border-slate-300 bg-slate-200 text-slate-600'
};

const slugifyOperationalValue = (value = '') =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const normalizeOperationalLoadStatus = (value = '') => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (OPERATIONAL_LOAD_STATUS_LABELS[normalized]) return normalized;
  if (normalized === 'pending') return 'pending_pickup';
  if (normalized === 'pickup') return 'picked_up';
  if (normalized === 'delivery') return 'delivered';
  return 'planned';
};

export const getDestinationOperationRoleLabel = (role = '') => {
  const normalized = String(role || '').trim().toLowerCase();
  return DESTINATION_OPERATION_ROLE_LABELS[normalized] || 'Operación';
};

export const getOperationalLoadStatusLabel = (status = '') => {
  const normalized = normalizeOperationalLoadStatus(status);
  return OPERATIONAL_LOAD_STATUS_LABELS[normalized] || 'Planificada';
};

export const getOperationalLoadStatusTone = (status = '') => {
  const normalized = normalizeOperationalLoadStatus(status);
  return OPERATIONAL_LOAD_STATUS_TONES[normalized] || OPERATIONAL_LOAD_STATUS_TONES.planned;
};

export const inferDestinationOperationRole = (destination = {}) => {
  const rawRole = String(destination?.operationRole || destination?.role || '')
    .trim()
    .toLowerCase();

  if (DESTINATION_OPERATION_ROLES.includes(rawRole)) {
    return rawRole;
  }

  const type = String(destination?.type || '')
    .trim()
    .toLowerCase();
  const deliveries = Number(destination?.deliveries || 0);
  const pickups = Number(destination?.pickups || 0);

  if (pickups > 0 && deliveries > 0) return 'mixed';
  if (pickups > 0) return 'pickup';
  if (deliveries > 0) return 'delivery';
  if (type.includes('retiro')) return 'pickup';
  if (type.includes('entrega')) return 'delivery';
  if (type.includes('devol')) return 'return';
  if (type.includes('guarda') || type.includes('almacen')) return 'storage';
  if (type.includes('traslado') || type.includes('transfer')) return 'transfer';
  if (destination?.deliversTo === 'oficina') return 'delivery';

  return 'delivery';
};

export const normalizeOperationalLoad = (load = {}, options = {}) => {
  const index = Number(options?.index ?? 0) || 0;
  const role = String(load?.operationType || load?.type || options?.operationType || '')
    .trim()
    .toLowerCase();
  const normalizedOperationType = OPERATIONAL_LOAD_OPERATION_TYPES.includes(role)
    ? role
    : 'delivery';

  const title = String(load?.title ?? load?.name ?? load?.product ?? '');
  const description = String(load?.description ?? '');
  const fallbackIdBase =
    slugifyOperationalValue(title.trim() || String(load?.referenceCode || '').trim() || `carga-${index + 1}`) ||
    `carga-${index + 1}`;

  return {
    id: String(load?.id || options?.fallbackId || `load-${fallbackIdBase}-${index + 1}`),
    title,
    description,
    quantity: Number(load?.quantity ?? load?.qty ?? 1) || 1,
    unit: String(load?.unit ?? options?.unit ?? 'unidad'),
    category: String(load?.category ?? ''),
    referenceCode: String(load?.referenceCode ?? ''),
    operationType: normalizedOperationType,
    status: normalizeOperationalLoadStatus(load?.status),
    originStopId: load?.originStopId || load?.originId || '',
    targetStopId: load?.targetStopId || load?.targetId || '',
    currentStopId: load?.currentStopId || '',
    purchaseId: String(load?.purchaseId ?? ''),
    purchaseLineId: String(load?.purchaseLineId ?? ''),
    purchaseNumber: String(load?.purchaseNumber ?? ''),
    supplierId: String(load?.supplierId ?? ''),
    supplierNameSnapshot: String(load?.supplierNameSnapshot ?? ''),
    stockSourceType: String(load?.stockSourceType ?? ''),
    stockMode: String(load?.stockMode ?? ''),
    stockProductId: String(load?.stockProductId ?? ''),
    stockProductNameSnapshot: String(load?.stockProductNameSnapshot ?? ''),
    stockSkuSnapshot: String(load?.stockSkuSnapshot ?? ''),
    stockBarcodeSnapshot: String(load?.stockBarcodeSnapshot ?? load?.barcodeSnapshot ?? ''),
    stockQrCodeSnapshot: String(load?.stockQrCodeSnapshot ?? load?.qrCodeSnapshot ?? ''),
    stockCategorySnapshot: String(load?.stockCategorySnapshot ?? ''),
    stockUnitSnapshot: String(load?.stockUnitSnapshot ?? load?.unit ?? options?.unit ?? 'unidad'),
    stockOperationReason: String(load?.stockOperationReason ?? ''),
    stockOutboundOrderId: String(load?.stockOutboundOrderId ?? ''),
    stockOutboundOrderNumber: String(load?.stockOutboundOrderNumber ?? ''),
    stockOutboundOrderLineId: String(load?.stockOutboundOrderLineId ?? ''),
    stockOrderReference: String(load?.stockOrderReference ?? ''),
    saleUnitPrice:
      load?.saleUnitPrice === '' || load?.saleUnitPrice == null
        ? null
        : Number(load.saleUnitPrice),
    requiresEvidenceOnPickup: Boolean(load?.requiresEvidenceOnPickup),
    requiresEvidenceOnDelivery: Boolean(load?.requiresEvidenceOnDelivery),
    pickupEvidence: Array.isArray(load?.pickupEvidence) ? load.pickupEvidence : [],
    deliveryEvidence: Array.isArray(load?.deliveryEvidence) ? load.deliveryEvidence : [],
    notes: String(load?.notes ?? ''),
    history: Array.isArray(load?.history) ? load.history : [],
    createdAt: load?.createdAt || null,
    updatedAt: load?.updatedAt || null,
    createdBy: load?.createdBy || ''
  };
};

export const normalizeDestinationOperationalFields = (destination = {}) => {
  const operationRole = inferDestinationOperationRole(destination);
  const operationalLoads = Array.isArray(destination?.operationalLoads)
    ? destination.operationalLoads.map((load, index) =>
        normalizeOperationalLoad(load, {
          index,
          operationType:
            operationRole === 'pickup'
              ? 'pickup'
              : operationRole === 'storage'
                ? 'storage'
                : operationRole === 'transfer'
                  ? 'transfer'
                  : operationRole === 'return'
                    ? 'return'
                    : 'delivery'
        })
      )
    : [];

  return {
    ...destination,
    operationRole,
    operationalLoads
  };
};

// --- Colores para vínculos entre destinos ---
export const generateLinkColors = (destinations) => {
  const colors = [
    '#e11d48',
    '#db2777',
    '#9333ea',
    '#4f46e5',
    '#1d4ed8',
    '#0284c7',
    '#059669',
    '#ca8a04'
  ];

  const links = {};
  let colorIndex = 0;

  (destinations || []).forEach((dest) => {
    if (dest.deliversTo && dest.deliversTo !== 'oficina') {
      if (!links[dest.id]) {
        const color = colors[colorIndex % colors.length];
        links[dest.id] = color;
        links[dest.deliversTo] = color;
        colorIndex++;
      }
    }
  });

  return links;
};

// --- Distancia Haversine en km ---
export const calculateDistance = (lat1, lon1, lat2, lon2) =>
  calculateStraightLineDistanceKm(
    { lat: lat1, lng: lon1 },
    { lat: lat2, lng: lon2 }
  ) || 0;

export const getRouteRoadMetrics = (route = {}) => {
  const metrics = route?.roadMetrics || route?.roadMetricsSnapshot || route?.distanceSnapshot || null;
  return metrics && typeof metrics === 'object' ? metrics : null;
};

const getRoadLegDistanceKm = ({ route = {}, destinationId = '', fallbackDistanceKm = 0 }) => {
  const roadMetrics = getRouteRoadMetrics(route);
  const fromMetrics = Number(roadMetrics?.legsByDestinationId?.[destinationId]?.distanceKm);
  if (Number.isFinite(fromMetrics) && fromMetrics >= 0) return fromMetrics;

  const destination = Array.isArray(route?.destinations)
    ? route.destinations.find((item) => item?.id === destinationId)
    : null;

  const fromDestination = Number(destination?.roadDistanceKm);
  if (Number.isFinite(fromDestination) && fromDestination >= 0) return fromDestination;

  return fallbackDistanceKm;
};



export const getRouteResolvedRoadSummary = ({
  route = {},
  completedOnly = true,
  officeLocation = OFFICE_LOCATION
}) => {
  const routeRoadMetrics = getRouteRoadMetrics(route);
  const eligibleDestinations = (Array.isArray(route?.destinations) ? route.destinations : [])
    .filter((dest) => {
      const normalizedLatLng = normalizeLatLng(dest?.latlng);
      if (!normalizedLatLng) return false;
      if (!completedOnly) return true;
      return dest?.status === 'completed' || dest?.status === 'incomplete';
    })
    .sort((a, b) => getComparableTime(a.completedAt) - getComparableTime(b.completedAt));

  if (!eligibleDestinations.length) {
    return {
      totalDistanceKm: 0,
      totalDurationMin: 0,
      source: routeRoadMetrics?.source || 'straight',
      hasRoadMetrics: Boolean(routeRoadMetrics)
    };
  }

  let totalDistanceKm = 0;
  let totalDurationMin = 0;
  let usedRoadMetrics = false;

  let previousPoint = normalizeLatLng(officeLocation) || normalizeLatLng(OFFICE_LOCATION);

  eligibleDestinations.forEach((dest) => {
    const currentPoint = normalizeLatLng(dest?.latlng);
    if (!previousPoint || !currentPoint) return;

    const roadLeg = routeRoadMetrics?.legsByDestinationId?.[dest.id] || null;
    const roadDistanceKm = Number(roadLeg?.distanceKm);
    const roadDurationMin = Number(roadLeg?.durationMin);

    if (Number.isFinite(roadDistanceKm) && roadDistanceKm >= 0) {
      totalDistanceKm += roadDistanceKm;
      usedRoadMetrics = true;
    } else {
      totalDistanceKm += calculateDistance(
        previousPoint.lat,
        previousPoint.lng,
        currentPoint.lat,
        currentPoint.lng
      );
    }

    if (Number.isFinite(roadDurationMin) && roadDurationMin >= 0) {
      totalDurationMin += roadDurationMin;
    }

    previousPoint = currentPoint;
  });

  return {
    totalDistanceKm,
    totalDurationMin,
    source: usedRoadMetrics ? routeRoadMetrics?.source || 'road' : 'straight',
    hasRoadMetrics: usedRoadMetrics
  };
};

export const buildRouteRoadMetrics = async ({
  route,
  officeLocation = OFFICE_LOCATION,
  completedOnly = true,
  signal
}) => {
  if (!route || !Array.isArray(route.destinations) || route.destinations.length === 0) {
    return null;
  }

  const eligibleDestinations = route.destinations
    .filter((dest) => {
      const normalizedLatLng = normalizeLatLng(dest?.latlng);
      if (!normalizedLatLng) return false;
      if (!completedOnly) return true;

      return dest?.status === 'completed' || dest?.status === 'incomplete';
    })
    .sort((a, b) => getComparableTime(a.completedAt) - getComparableTime(b.completedAt));

  if (!eligibleDestinations.length) return null;

  const officePoint = normalizeLatLng(officeLocation) || normalizeLatLng(OFFICE_LOCATION);
  if (!officePoint) return null;

  const stops = [officePoint, ...eligibleDestinations.map((dest) => normalizeLatLng(dest.latlng)).filter(Boolean)];
  const roadRoute = await buildRoadRouteForStops(stops, { signal });

  const destinationSignature = eligibleDestinations
    .map((dest) => `${dest.id || ''}:${Number(dest.order ?? 0)}:${Number(dest?.latlng?.lat || 0).toFixed(6)}:${Number(dest?.latlng?.lng || 0).toFixed(6)}`)
    .join('|');

  const legsByDestinationId = {};
  eligibleDestinations.forEach((dest, index) => {
    const segment = roadRoute?.segments?.[index];
    if (!segment) return;

    legsByDestinationId[dest.id] = {
      distanceKm: Number(segment.distanceKm || 0),
      durationMin: Number(segment.durationMin || 0),
      source: segment.source || roadRoute?.source || 'road'
    };
  });

  return {
    source: roadRoute?.source || 'road',
    computedAt: new Date().toISOString(),
    totalDistanceKm: Number(roadRoute?.distanceKm || 0),
    totalDurationMin: Number(roadRoute?.durationMin || 0),
    geometry: Array.isArray(roadRoute?.geometry) ? roadRoute.geometry : [],
    destinationSignature,
    legsByDestinationId
  };
};

// --- Geocodificación con Nominatim ---
export const geocodeAddress = async (address, showMessage) => {
  const encodedAddress = encodeURIComponent(address);
  const url = `https://nominatim.openstreetmap.org/search?q=${encodedAddress}&format=json&limit=1&countrycodes=ar`;

  try {
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'LogisticaSTIApp/1.0 (sti.logistica.app@gmail.com)'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (
      data &&
      data.length > 0 &&
      typeof data[0].lat === 'string' &&
      typeof data[0].lon === 'string'
    ) {
      const lat = parseFloat(data[0].lat);
      const lng = parseFloat(data[0].lon);

      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }

    showMessage?.(
      'Dirección no encontrada. Intenta ser más específico (ej: agregar Ciudad/Localidad).',
      'warning'
    );
    return null;
  } catch (error) {
    console.error('Error en geocodeAddress catch:', error);
    showMessage?.(
      'Error al obtener coordenadas. Verifica la dirección e intenta de nuevo.',
      'error'
    );
    return null;
  }
};

// =====================================================
// Liquidación por tipo de transporte
// Compatible con configuración legacy global
// =====================================================

export const DEFAULT_TRANSPORT_TYPES = ['moto', 'camioneta', 'auto', 'camion'];

export const DEFAULT_TRANSPORT_LIQUIDATION = {
  costModel: 'perKm',
  pricePerKm: 0,
  baseRouteFee: 0,
  minimumRouteFee: 0,
  costScales: []
};

export const DEFAULT_LIQUIDATION_SETTINGS = {
  enabled: true,
  defaultCostModel: 'perKm',
  defaultPricePerKm: 0,
  transports: {
    moto: { ...DEFAULT_TRANSPORT_LIQUIDATION },
    camioneta: { ...DEFAULT_TRANSPORT_LIQUIDATION },
    auto: { ...DEFAULT_TRANSPORT_LIQUIDATION },
    camion: { ...DEFAULT_TRANSPORT_LIQUIDATION }
  }
};

const normalizeScale = (scale = {}) => ({
  from:
    typeof scale.from === 'string'
      ? Number(
          scale.from
            .trim()
            .replace(/\s+/g, '')
            .replace(/\.(?=\d{3}(?:\D|$))/g, '')
            .replace(',', '.')
        ) || 0
      : Number(scale.from ?? 0) || 0,
  to:
    scale.to === '' || scale.to === null || scale.to === undefined
      ? null
      : typeof scale.to === 'string'
        ? Number(
            scale.to
              .trim()
              .replace(/\s+/g, '')
              .replace(/\.(?=\d{3}(?:\D|$))/g, '')
              .replace(',', '.')
          ) || 0
        : Number(scale.to) || 0,
  price:
    typeof scale.price === 'string'
      ? Number(
          scale.price
            .trim()
            .replace(/\s+/g, '')
            .replace(/\.(?=\d{3}(?:\D|$))/g, '')
            .replace(',', '.')
        ) || 0
      : Number(scale.price ?? 0) || 0
});

const parseLocalizedNumber = (value, fallback = 0) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value === 'string') {
    const normalizedText = value.trim();
    if (!normalizedText) return fallback;

    const sanitized = normalizedText
      .replace(/\s+/g, '')
      .replace(/\.(?=\d{3}(?:\D|$))/g, '')
      .replace(',', '.');

    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizeLiquidationSettings = (rawSettings = {}) => {
  const hasNestedLiquidation =
    rawSettings?.liquidationSettings && typeof rawSettings.liquidationSettings === 'object';

  const hasDirectLiquidationShape =
    rawSettings &&
    typeof rawSettings === 'object' &&
    (
      rawSettings.transports ||
      rawSettings.defaultCostModel ||
      rawSettings.defaultPricePerKm !== undefined ||
      rawSettings.enabled !== undefined
    );

  const liquidationSettings = hasNestedLiquidation
    ? rawSettings.liquidationSettings
    : hasDirectLiquidationShape
      ? rawSettings
      : {};

  const rawTransports = liquidationSettings?.transports || rawSettings?.transports || {};

  const normalized = {
    enabled: liquidationSettings.enabled ?? rawSettings?.enabled ?? true,
    defaultCostModel:
      liquidationSettings.defaultCostModel ||
      rawSettings?.defaultCostModel ||
      rawSettings?.costModel ||
      DEFAULT_LIQUIDATION_SETTINGS.defaultCostModel,
    defaultPricePerKm:
      parseLocalizedNumber(
        liquidationSettings.defaultPricePerKm ??
          rawSettings?.defaultPricePerKm ??
          rawSettings?.pricePerKm ??
          DEFAULT_LIQUIDATION_SETTINGS.defaultPricePerKm
      ) || 0,
    transports: {}
  };

  DEFAULT_TRANSPORT_TYPES.forEach((type) => {
    const rawTypeSettings = rawTransports[type] || {};

    normalized.transports[type] = {
      ...DEFAULT_TRANSPORT_LIQUIDATION,
      ...rawTypeSettings,
      costModel:
        rawTypeSettings.costModel ||
        normalized.defaultCostModel ||
        'perKm',
      pricePerKm:
        Number(
          rawTypeSettings.pricePerKm ??
            normalized.defaultPricePerKm ??
            0
        ) || 0,
      baseRouteFee: Number(rawTypeSettings.baseRouteFee ?? 0) || 0,
      minimumRouteFee: Number(rawTypeSettings.minimumRouteFee ?? 0) || 0,
      costScales: Array.isArray(rawTypeSettings.costScales)
        ? rawTypeSettings.costScales.map(normalizeScale)
        : []
    };
  });

  return normalized;
};

export const getTransportTypeFromRoute = (route = {}) => {
  return (
    route?.transportType ||
    route?.transport ||
    route?.transporterType ||
    ''
  )
    .toString()
    .trim()
    .toLowerCase();
};

export const getTransportLiquidationOverride = (transport = null) => {
  if (!transport || transport?.liquidation?.mode !== 'custom') {
    return null;
  }

  const customSettings = transport?.liquidation?.customSettings;
  if (!customSettings) return null;

  return {
    ...DEFAULT_TRANSPORT_LIQUIDATION,
    ...customSettings,
    costModel: customSettings.costModel || 'perKm',
    pricePerKm: Number(customSettings.pricePerKm ?? 0) || 0,
    baseRouteFee: Number(customSettings.baseRouteFee ?? 0) || 0,
    minimumRouteFee: Number(customSettings.minimumRouteFee ?? 0) || 0,
    costScales: Array.isArray(customSettings.costScales)
      ? customSettings.costScales.map(normalizeScale)
      : []
  };
};

const normalizeResolvedLiquidationConfig = (config = {}, fallback = {}) => ({
  ...DEFAULT_TRANSPORT_LIQUIDATION,
  ...fallback,
  ...config,
  costModel:
    config?.costModel === 'byScale'
      ? 'byScale'
      : fallback?.costModel === 'byScale'
        ? 'byScale'
        : 'perKm',
  pricePerKm: Number(config?.pricePerKm ?? fallback?.pricePerKm ?? 0) || 0,
  baseRouteFee: Number(config?.baseRouteFee ?? fallback?.baseRouteFee ?? 0) || 0,
  minimumRouteFee: Number(config?.minimumRouteFee ?? fallback?.minimumRouteFee ?? 0) || 0,
  costScales: Array.isArray(config?.costScales)
    ? config.costScales.map(normalizeScale)
    : Array.isArray(fallback?.costScales)
      ? fallback.costScales.map(normalizeScale)
      : []
});

export const getRouteLiquidationSnapshot = (route = {}) => {
  const snapshot = route?.liquidationSnapshot;

  if (!snapshot || typeof snapshot !== 'object') {
    return null;
  }

  const transportType =
    (snapshot.transportType || getTransportTypeFromRoute(route) || '')
      .toString()
      .trim()
      .toLowerCase();

  return {
    source: snapshot.source || 'routeSnapshot',
    transportType,
    config: {
      ...normalizeResolvedLiquidationConfig(snapshot, DEFAULT_TRANSPORT_LIQUIDATION),
      mode: snapshot.mode || 'inherit',
      transportId: snapshot.transportId || route?.transporterId || '',
      transportSlug: snapshot.transportSlug || route?.transporterSlug || '',
      transportName: snapshot.transportName || route?.transporterName || '',
      savedAt: snapshot.savedAt || null
    }
  };
};

export const resolveLiquidationConfig = ({ route, settings, transport = null }) => {
  const normalizedSettings = normalizeLiquidationSettings(settings);
  const transportType = getTransportTypeFromRoute(route);
  const snapshotConfig = getRouteLiquidationSnapshot(route);
  const overrideConfig = getTransportLiquidationOverride(transport);

  if (snapshotConfig) {
    return snapshotConfig;
  }

  if (overrideConfig) {
    return {
      source: 'transportOverride',
      transportType,
      config: overrideConfig
    };
  }

  if (transportType && normalizedSettings.transports[transportType]) {
    return {
      source: 'transportType',
      transportType,
      config: normalizedSettings.transports[transportType]
    };
  }

  return {
    source: 'legacyFallback',
    transportType,
    config: {
      costModel:
        settings?.costModel ||
        settings?.defaultCostModel ||
        normalizedSettings.defaultCostModel ||
        'perKm',
      pricePerKm:
        Number(
          settings?.pricePerKm ??
            settings?.defaultPricePerKm ??
            normalizedSettings.defaultPricePerKm ??
            0
        ) || 0,
      baseRouteFee: 0,
      minimumRouteFee: 0,
      costScales: Array.isArray(settings?.costScales)
        ? settings.costScales.map(normalizeScale)
        : []
    }
  };
};

const getComparableTime = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  return 0;
};

const findScalePrice = (distanceKm, costScales = []) => {
  const matchedScale = costScales.find((scale) => {
    const from = Number(scale.from ?? 0) || 0;
    const to =
      scale.to === '' || scale.to === null || scale.to === undefined
        ? null
        : Number(scale.to) || 0;

    if (to === null) return distanceKm >= from;
    return distanceKm >= from && distanceKm < to;
  });

  return matchedScale ? Number(matchedScale.price ?? 0) || 0 : 0;
};

export const calculateRouteCost = ({
  route,
  settings,
  transport = null,
  officeLocation = OFFICE_LOCATION
}) => {
  if (!route || !Array.isArray(route.destinations) || route.destinations.length === 0) {
    return {
      totalCost: 0,
      totalDistanceKm: 0,
      legCosts: {},
      liquidationConfig: null
    };
  }

  const { config, source, transportType } = resolveLiquidationConfig({
    route,
    settings,
    transport
  });

  const completedDestinations = route.destinations
    .filter(
      (dest) =>
        (dest.status === 'completed' || dest.status === 'incomplete') &&
        dest.latlng &&
        typeof dest.latlng.lat === 'number' &&
        typeof dest.latlng.lng === 'number'
    )
    .sort((a, b) => getComparableTime(a.completedAt) - getComparableTime(b.completedAt));

  if (completedDestinations.length === 0) {
    return {
      totalCost: 0,
      totalDistanceKm: 0,
      legCosts: {},
      roadMetrics: getRouteRoadMetrics(route),
      liquidationConfig: {
        ...config,
        source,
        transportType
      }
    };
  }

  let totalCost = 0;
  let totalDistanceKm = 0;
  const legCosts = {};

  let previousPoint = {
    lat: officeLocation.lat,
    lng: officeLocation.lng
  };

  completedDestinations.forEach((dest) => {
    const fallbackDistanceKm = calculateDistance(
      previousPoint.lat,
      previousPoint.lng,
      dest.latlng.lat,
      dest.latlng.lng
    );
    const distanceKm = getRoadLegDistanceKm({
      route,
      destinationId: dest.id,
      fallbackDistanceKm
    });

    let legCost = 0;

    if (config.costModel === 'perKm') {
      legCost = distanceKm * (Number(config.pricePerKm) || 0);
    } else if (config.costModel === 'byScale') {
      legCost = findScalePrice(distanceKm, config.costScales);
    }

    totalDistanceKm += distanceKm;
    totalCost += legCost;
    legCosts[dest.id] = legCost;

    previousPoint = dest.latlng;
  });

  totalCost += Number(config.baseRouteFee || 0);

  if (Number(config.minimumRouteFee || 0) > 0) {
    totalCost = Math.max(totalCost, Number(config.minimumRouteFee || 0));
  }

  return {
    totalCost,
    totalDistanceKm,
    legCosts,
    roadMetrics: getRouteRoadMetrics(route),
    liquidationConfig: {
      ...config,
      source,
      transportType
    }
  };
};
